import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import CircularProgress from '@material-ui/core/CircularProgress'
import ApiService from '../../ApiService'
import RemoveCSS from '../../util/RemoveCSS'
import url from '../../util/routeURL'
import useUserStore from '../../hook/useUserStore'
import { decodeIdToken, decodeToken, getAccessToken } from '../../util/authUtil'
import '../dashboard/style.css'
import './style.css'

const consentLibrary = {
  terms: { id: 'terms', key: 'termsOfService', label: 'Terms of Service', href: 'https://www.tbd.health/terms' },
  privacy: { id: 'privacy', key: 'privacyPolicy', label: 'Privacy Policy', href: 'https://www.tbd.health/privacy-policy' },
  telehealth: { id: 'telehealth', key: 'telehealthConsent', label: 'Telehealth Consent', href: 'https://www.tbd.health/consent-to-telehealth' },
  prep: { id: 'prep', key: 'prepConsent', label: 'PrEP Consent (if applicable)', href: 'https://www.tbd.health/prep-consent' },
  pep: { id: 'pep', key: 'pepConsent', label: 'PEP Consent (if applicable)', href: 'https://www.tbd.health/pep-consent' },
  identity: {
    id: 'identity',
    key: 'identityVerificationTruthfulness',
    label: 'Identity Verification & Truthfulness Consent',
    href: 'https://www.tbd.health/identity-consent',
  },
}

const allConsentItems = [consentLibrary.terms, consentLibrary.privacy, consentLibrary.telehealth, consentLibrary.prep, consentLibrary.pep, consentLibrary.identity]
const defaultConsentItems = [consentLibrary.prep, consentLibrary.identity]

const hasAllRequiredConsents = (consents, requiredKeys) => {
  if (!consents || !requiredKeys?.length) return false
  return requiredKeys.every(key => !!consents[key])
}

const Consents = () => {
  const history = useHistory()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [userClaims, setUserClaims] = useState(null)
  const [patientProfile, setPatientProfile] = useState(null)
  const [needsConsent, setNeedsConsent] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [consentState, setConsentState] = useState({})
  const existingConsents = useMemo(() => patientProfile?.consents || {}, [patientProfile])
  const setPatient = useUserStore(state => state.setPatient)
  const setIsAuth = useUserStore(state => state.setIsAuth)

  const selectedConsentItems = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const typeParam = (params.get('type') || '').toLowerCase()
    if (!typeParam) return defaultConsentItems

    const requested = typeParam
      .split(',')
      .map(entry => entry.trim().toLowerCase())
      .filter(Boolean)

    if (requested.includes('all')) return allConsentItems

    const mapped = requested
      .map(key => consentLibrary[key])
      .filter(Boolean)
      // remove duplicates while preserving order
      .filter((item, idx, arr) => arr.findIndex(found => found.id === item.id) === idx)

    return mapped.length > 0 ? mapped : defaultConsentItems
  }, [location.search])

  const requiredConsentKeys = useMemo(() => selectedConsentItems.map(item => item.key), [selectedConsentItems])

  useEffect(() => {
    RemoveCSS()
    document.body.classList.add('body_portal')
    return () => {
      document.body.classList.remove('body_portal')
    }
  }, [])

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true)
      setError(null)

      try {
        const accessToken = await getAccessToken()
        // Gate on the access token's `sub` (always present on a valid session),
        // consistent with the route guards. The ID token is read separately for
        // display claims (given_name, etc.) — access tokens don't carry those.
        if (!accessToken || !decodeToken(accessToken)?.sub) {
          setIsAuth(false)
          return
        }
        setUserClaims(decodeIdToken())

        const patientRes = await ApiService.getPatientAfterSessRef()
        const profile = patientRes.data
        setPatientProfile(profile)
        setPatient(profile)

        setNeedsConsent(!hasAllRequiredConsents(profile?.consents, requiredConsentKeys))
      } catch (err) {
        console.error('Error loading consents', err)
        setError('We were unable to load your consent information. Please refresh and try again.')
      } finally {
        setLoading(false)
      }
    }

    initializePage()
  }, [requiredConsentKeys, setPatient, setIsAuth])

  const buildConsentPayload = latestConsents => {
    const timestamp = new Date().toISOString()
    const nextConsents = { ...(latestConsents || {}) }
    selectedConsentItems.forEach(item => {
      if (!nextConsents[item.key]) {
        nextConsents[item.key] = timestamp
      }
    })
    return nextConsents
  }

  const handleConsentSubmit = async () => {
    if (submitting || !allChecked) return
    setSubmitting(true)
    setError(null)

    try {
      const patientRes = await ApiService.getPatientAfterSessRef()
      const latestProfile = patientRes.data
      const updatedConsents = buildConsentPayload(latestProfile?.consents)
      const payload = { ...latestProfile, consents: updatedConsents }

      const updatedUserDetails = await ApiService.updateUserHealthData(payload).then(resp => resp.data)
      setPatientProfile(updatedUserDetails)
      setPatient(updatedUserDetails)
      setNeedsConsent(false)
      history.push(url.home)
    } catch (err) {
      console.error('Error updating user consents', err)
      setError('We could not save your consents. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleGoHome = () => history.push(url.home)

  useEffect(() => {
    const resetState = selectedConsentItems.reduce((acc, item) => {
      acc[item.id] = !!existingConsents[item.key]
      return acc
    }, {})
    setConsentState(resetState)
  }, [selectedConsentItems, existingConsents])

  const allChecked = useMemo(() => selectedConsentItems.every(item => consentState[item.id]), [selectedConsentItems, consentState])

  const handleToggle = (id, checked) => {
    setConsentState(prev => ({ ...prev, [id]: checked }))
  }

  if (loading) {
    return (
      <div className="consent-page consent-page__loader">
        <CircularProgress className="loading-button-spinner" />
      </div>
    )
  }

  const showCompletionMessage = !needsConsent && patientProfile && !error
  const showBannerError = !!error && (!needsConsent || !patientProfile)

  return (
    <div className="consent-page">
      {showBannerError && <div className="consent-page__error">{error}</div>}

      {needsConsent && (
        <div className="consent-card consent-card--page">
          <div className="consent-card__inner">
            <div className="consent-card__tag">Action needed</div>
            <h2 className="consent-card__title">Updates to TBD Health consents</h2>
            <p className="consent-card__greeting">Hi {patientProfile?.firstName || userClaims?.given_name || 'there'},</p>
            <p className="consent-card__copy">
              Thanks for trusting TBD Health. We've added a few more consents to our process. Please review them below and check the boxes if you agree. This keeps
              your access to TBD's services active.
            </p>

            <div className="consent-card__box">
              {selectedConsentItems.map(item => {
                const alreadyConsented = !!existingConsents[item.key]
                return (
                  <div key={item.id} className="consent-card__checkbox form-check">
                    <input
                      type="checkbox"
                      id={`${item.id}-consent-checkbox`}
                      className="form-check-input"
                      checked={!!consentState[item.id]}
                      onChange={e => handleToggle(item.id, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`${item.id}-consent-checkbox`}>
                      {item.label}{' '}
                      <a href={item.href} target="_blank" rel="noreferrer">
                        (view)
                      </a>
                      {alreadyConsented && <span className="consent-card__status">(already completed)</span>}
                    </label>
                  </div>
                )
              })}
            </div>

            <p className="consent-card__note">
              If you have questions, please let us know.
              <br />
              All the best,
              <br />
              Team TBD
            </p>

            {error && <p className="consent-card__error">{error}</p>}

            <div className="consent-card__actions">
              <button type="button" className="consent-card__cta" onClick={handleConsentSubmit} disabled={!allChecked || submitting}>
                {submitting ? 'Saving...' : 'Agree & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompletionMessage && (
        <div className="consent-page__message">
          <h2>You're all set</h2>
          <p>Your consent preferences are already up to date.</p>
          <button type="button" className="consent-page__cta" onClick={handleGoHome}>
            Go to dashboard
          </button>
        </div>
      )}
    </div>
  )
}

export default Consents
